// chatActiveChats.ts
import type { Message, User } from "./chatTypes";
import type { LastMessageType } from "./chatMessageUtils";
import { deriveType } from "./chatMessageUtils";
import { idOf } from "./idOf";

export type ChatListItem = User & {
  unread?: number;
  lastMessageType?: LastMessageType;
  lastMessage?: string;
  lastMessageAt?: string | number | Date;
};

export type ChatPreviewLabels = {
  message: string;
  photo: string;
  video: string;
  audio: string;
  media: string;
  fallbackUser: string;
};

export function toEpochMs(input: unknown): number | null {
  if (input == null) return null;

  // Date
  if (input instanceof Date) return input.getTime();

  // number (sec or ms)
  if (typeof input === "number") return input < 1e12 ? input * 1000 : input;

  // string (ISO or numeric)
  if (typeof input === "string") {
    const n = Number(input);
    if (!Number.isNaN(n)) return n < 1e12 ? n * 1000 : n;
    const parsed = Date.parse(input);
    return Number.isNaN(parsed) ? null : parsed;
  }

  // Firestore Timestamp: { toDate() }
  if (typeof input === "object" && input && "toDate" in input && typeof (input as any).toDate === "function") {
    const d = (input as any).toDate();
    return d instanceof Date ? d.getTime() : null;
  }

  // Firestore-style {seconds} or {_seconds}
  if (typeof input === "object" && input) {
    const sec = (input as any).seconds ?? (input as any)._seconds;
    if (typeof sec === "number") return sec * 1000;
  }

  return null;
}

export function nextActiveChatsAfterIncoming(
  prev: ChatListItem[],
  base: Message,
  meId: string,
  isOpenThread: boolean,
  labels: ChatPreviewLabels
): ChatListItem[] {
  const iAmSender = idOf(base.sender) === meId;
  const partnerId = iAmSender ? idOf(base.recipient) : idOf(base.sender);

  const idx = prev.findIndex((c) => c._id === partnerId);
  const prevRow = idx >= 0 ? prev[idx] : undefined;

  const lmt = deriveType(base);

  const raw =
  (base as any)?.createdAt ??
  (base as any)?.timestamp ??
  (base as any)?.sentAt ??
  Date.now();

const lastMessageAt = toEpochMs(raw) ?? Date.now();

  const preview =
    lmt === "text"
      ? (base.decryptedText ?? base.text ?? "").trim() || labels.message
      : lmt === "image"
      ? labels.photo
      : lmt === "video"
      ? labels.video
      : lmt === "audio"
      ? labels.audio
      : labels.media;

  const isPlaceholderText = lmt === "text" && preview === labels.message;
  const keepPrev =
    !!prevRow &&
    prevRow.lastMessageType &&
    prevRow.lastMessageType !== "text" &&
    isPlaceholderText;

  const isAlbum = (base as any).albumIds && (base as any).albumIds.length;
  const inc = !isOpenThread && !iAmSender && !isAlbum ? 1 : 0;
  const unreadBase = idx >= 0 ? prev[idx].unread || 0 : 0;

  const displayName =
    idx >= 0
      ? prev[idx].profilename
      : (iAmSender ? (base.recipient as any)?.profilename : (base.sender as any)?.profilename) ||
        labels.fallbackUser;

  const displayPic =
    idx >= 0
      ? prev[idx].picture
      : (iAmSender ? (base.recipient as any)?.picture : (base.sender as any)?.picture) || "";

  const nextRow: ChatListItem = keepPrev
  ? { ...prevRow!, unread: Math.max(0, unreadBase + inc), lastMessageAt }
  : {
      _id: partnerId,
      profilename: displayName,
      picture: displayPic,
      lastMessage: preview,
      lastMessageType: lmt,
      unread: Math.max(0, unreadBase + inc),
      lastMessageAt,
    };

  let next =
    idx >= 0 ? prev.map((c, i) => (i === idx ? nextRow : c)) : [nextRow, ...prev];

  // move to top
  const picked = next.find((c) => c._id === partnerId)!;
  next = [picked, ...next.filter((c) => c._id !== partnerId)];
  return next;
}

export function formatChatTimestamp(input?: unknown, locale = "en-US"): string {
  const ms = toEpochMs(input);
  if (!ms) return "";

  const d = new Date(ms);
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfThatDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((startOfToday.getTime() - startOfThatDay.getTime()) / 86400000);

  if (dayDiff === 0) {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false, });
  }
  if (dayDiff === 1) return "Yesterday";

  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "2-digit" });
}
    